import {
  Required,
  IsString,
  IsNumber,
  IsArray,
  DataValidator,
  transformKeys,
  ValidateNested,
} from "@/utils/type-vaildator";
import api from "./api";

interface GetAllFolderItemsResponse {
  folder_list: Array<any>;
  item_list: Array<any>;
}

class LatestImagesData {
  @Required("Item url cannot be empty")
  @IsString("Item url must be a string")
  itemUrl: string;
}

export class AllFolderItemData {
  @Required("Id cannot be empty")
  @IsNumber("Id must be a number")
  id: number;

  @Required("Folder name cannot be empty")
  @IsString("Folder name must be a string")
  folderName: string;

  @Required("Create time cannot be empty")
  @IsNumber("Create time must be a number")
  createTime: number;

  @IsNumber("Update time must be a number")
  updateTime?: number;

  @IsNumber("User id must be a number")
  userId?: number;

  @IsNumber("Folder id must be a number")
  folderId?: number;

  @IsNumber("Agent id must be a number")
  agentId?: number;

  @IsNumber("Item count must be a number")
  itemCount?: number;

  @IsNumber("Type must be a number")
  type?: number;

  @IsArray("Latest images must be an array")
  latestImages?: Array<LatestImagesData>;
}

export class MaterialItemData {
  @Required("Id cannot be empty")
  @IsNumber("Id must be a number")
  id: number;

  @Required("Folder id cannot be empty")
  @IsNumber("Folder id must be a number")
  folderId: number;

  @Required("Item url cannot be empty")
  @IsString("Item url must be a string")
  itemUrl: string;

  @Required("Item type cannot be empty")
  @IsString("Item type must be a string")
  itemType: string;

  @IsNumber("Create time must be a number")
  createTime: number;

  @IsString("Vector library file url must be a string")
  vectorLibraryFileUrl: string;

  @Required("Create time cannot be empty")
  @IsNumber("Duration must be a number")
  duration: number;

  @IsArray("Tags must be an array")
  tags: Array<TagData>;

  videoCoverUrl?: string;

  imageCoverUrl?: string;
};

export class TagData {
  @Required("Tag id cannot be empty")
  @IsNumber("Tag id must be a number")
  id: number;

  @Required("Tag name cannot be empty")
  @IsString("Tag name must be a string")
  tagName: string;

  @IsNumber("Folder id must be a number")
  folderId?: number;
};

class GetAllFolderItemsData {
  @Required("Folder list cannot be empty")
  @IsArray("Folder list must be an array")
  @ValidateNested(AllFolderItemData)
  folderList: Array<AllFolderItemData>;

  @Required("Item list cannot be empty")
  @IsArray("Item list must be an array")
  @ValidateNested(MaterialItemData)
  itemList: Array<MaterialItemData>;
}

const transformAndValidateData = (
  response: GetAllFolderItemsResponse
): GetAllFolderItemsData => {
  const transformed = transformKeys(response);
  const data = DataValidator.validateOrThrow(transformed, GetAllFolderItemsData);
  if (data?.itemList?.length) {
    data.itemList.forEach((item) => {
      if (item.itemType === 'video') {
        // 视频封面
        item.videoCoverUrl = `${item.itemUrl}?x-oss-process=video/snapshot,h_${Math.floor(250 * window.devicePixelRatio)},t_0,f_png,m_fast,ar_auto`;
      }
      if (item.itemType === 'image') {
        // 图片封面
        item.imageCoverUrl = `${item.itemUrl}?x-oss-process=image/resize,s_${Math.floor(250 * window.devicePixelRatio)}/auto-orient,1`;
      }
    })
  }
  return data;
};

export interface GetAllFolderItemsRequest {
  agentId?: string;
  type?: "" | "video" | "podcast" | "moments" | "post" | "article";
  parentFolderId?: number;
}

export const userMaterialGetAllFolderItems = async ({
  agentId = "",
  type = "",
  parentFolderId
}: GetAllFolderItemsRequest): Promise<GetAllFolderItemsData> => {
  const allowed = ["", "video", "podcast", "moments", "post", "article"] as const;
  const typeMap = { video: 1, podcast: 2, moments: 3, post: 4, article: 5 } as const;

  if (!allowed.includes(type)) {
    throw new Error("Invalid type");
  }
  if (agentId && !type) {
    throw new Error("Invalid type: agentId provided requires type");
  }
  if (type && !(type in typeMap)) {
    throw new Error("Invalid type");
  }

  const params: Record<string, any> = {};
  if (agentId) params.agent_id = agentId;
  if (type) params.type = typeMap[type as keyof typeof typeMap];
  if (parentFolderId) params.parent_folder_id = parentFolderId;

  const response = await api.post<GetAllFolderItemsResponse>("/user_material/get_all_folder_items", params);
  const data = transformAndValidateData(response.data);
  return data;
};